import { Inject, Injectable, LOCALE_ID, Optional } from '@angular/core';
import { formatDate } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { DataService } from './data.service';
import { UtilityService } from './utility.service';
import { Global } from '../_constants/global.variables';

export type MobileTimeZoneMode = 'local' | 'site';

@Injectable({ providedIn: 'root' })
export class MobileTimezoneService {
  private readonly storageKey = 'iops.mobile.timezoneMode';
  private readonly modeSubject = new BehaviorSubject<MobileTimeZoneMode>(this.readInitialMode());

  readonly mode$ = this.modeSubject.asObservable();

  constructor(
    @Optional() private dataService: DataService | null,
    @Optional() private utilityService: UtilityService | null,
    @Inject(LOCALE_ID) private locale: string
  ) {
    Global.User.currentUser.MobileTimeZoneMode = this.modeSubject.value;
  }

  get mode(): MobileTimeZoneMode {
    return this.modeSubject.value;
  }

  setMode(mode: MobileTimeZoneMode): void {
    if (this.modeSubject.value === mode) {
      return;
    }

    this.modeSubject.next(mode);
    Global.User.currentUser.MobileTimeZoneMode = mode;

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.storageKey, mode);
    }
  }

  getIndicatorLabel(): string {
    return this.mode === 'site' ? 'Site Time' : 'Local Time';
  }

  format(
    value: string | number | Date | null | undefined,
    format = 'medium',
    siteId?: number | null,
    includeSuffix = true
  ): string | null {
    const parsed = this.coerceDate(value);
    if (!parsed) {
      return null;
    }

    // Normal UI text should be formatted directly from the real instant in the
    // target IANA timezone. Only offset-only fallbacks use a shifted Date.
    const siteTimeZone = this.mode === 'site' ? this.getSiteTimeZoneName(siteId) : null;
    const displayDate = this.mode === 'site' && !siteTimeZone
      ? this.toSiteDate(parsed, siteId)
      : parsed;

    const formatted = siteTimeZone
      ? this.formatInTimeZone(parsed, format, siteTimeZone)
      : formatDate(displayDate, format, this.locale);
    if (!includeSuffix) {
      return formatted;
    }

    const suffix = this.getShortSuffix(parsed, siteId);
    return suffix ? `${formatted} ${suffix}` : formatted;
  }

  formatWallClock(
    wallClockValue: string | number | Date | null | undefined,
    format = 'medium',
    siteId?: number | null,
    includeSuffix = true,
    suffixSourceValue?: string | number | Date | null | undefined
  ): string | null {
    const parsedWallClock = this.coerceDate(wallClockValue);
    if (!parsedWallClock) {
      return null;
    }

    const formatted = formatDate(parsedWallClock, format, this.locale);
    if (!includeSuffix) {
      return formatted;
    }

    const suffixSource = this.coerceDate(suffixSourceValue ?? wallClockValue) ?? parsedWallClock;
    const suffix = this.getShortSuffix(suffixSource, siteId);
    return suffix ? `${formatted} ${suffix}` : formatted;
  }

  private readInitialMode(): MobileTimeZoneMode {
    if (typeof localStorage === 'undefined') {
      return 'local';
    }

    const stored = localStorage.getItem(this.storageKey);
    return stored === 'site' ? 'site' : 'local';
  }

  private coerceDate(value: string | number | Date | null | undefined): Date | null {
    if (value == null) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private toSiteDate(value: Date, siteId?: number | null): Date {
    const targetSiteId = this.resolveSiteId(siteId);
    if (targetSiteId == null || !this.dataService) {
      return value;
    }

    const cache = this.dataService.cache;
    const site = cache?.sitesObject?.[targetSiteId]
      ?? cache?.sites?.find((item: any) => item?.Id === targetSiteId);
    const offset = site?.UTCTimeOffset != null ? Number(site.UTCTimeOffset) : undefined;

    if (typeof offset !== 'number' || Number.isNaN(offset)) {
      return value;
    }

    return this.utilityService?.convertMillisecondsToDateWithTimezoneOffset(value.getTime(), offset) ?? value;
  }

  private getShortSuffix(value: Date, siteId?: number | null): string {
    if (this.mode === 'site') {
      // In site mode, prefer the site IANA zone, then UTC offset, and finally UTC
      // so the UI never silently implies local time when site metadata is missing.
      const siteTimeZone = this.getSiteTimeZoneName(siteId);
      if (siteTimeZone) {
        return this.getIntlShortName(value, siteTimeZone);
      }

      const siteOffset = this.getSiteUtcOffset(siteId);
      if (typeof siteOffset === 'number' && Number.isFinite(siteOffset)) {
        return this.formatUtcOffset(siteOffset);
      }

      return 'UTC';
    }

    const localTimeZone = this.getLocalTimeZoneName();
    return this.getIntlShortName(value, localTimeZone);
  }

  private getSiteTimeZoneName(siteId?: number | null): string | null {
    const site = this.getSite(siteId);
    return typeof site?.TimeZone === 'string' && site.TimeZone.trim()
      ? site.TimeZone.trim()
      : null;
  }

  private getSiteUtcOffset(siteId?: number | null): number | null {
    const site = this.getSite(siteId);
    const offset = site?.UTCTimeOffset != null ? Number(site.UTCTimeOffset) : null;
    return typeof offset === 'number' && Number.isFinite(offset)
      ? offset
      : null;
  }

  private getSite(siteId?: number | null): any | null {
    const targetSiteId = this.resolveSiteId(siteId);
    if (targetSiteId == null || !this.dataService) {
      return null;
    }

    const cache = this.dataService.cache;
    return cache?.sitesObject?.[targetSiteId]
      ?? cache?.sites?.find((item: any) => item?.Id === targetSiteId)
      ?? null;
  }

  private getLocalTimeZoneName(): string | undefined {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return undefined;
    }
  }

  private getIntlShortName(value: Date, timeZone?: string): string {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short'
      });
      const raw = formatter.formatToParts(value).find(part => part.type === 'timeZoneName')?.value ?? '';
      return this.normalizeTimeZoneSuffix(raw);
    } catch {
      return '';
    }
  }

  private formatInTimeZone(value: Date, format: string, timeZone: string): string {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });
      const parts = formatter.formatToParts(value);
      const map = new Map(parts.map(part => [part.type, part.value]));

      const year = Number(map.get('year'));
      const month = Number(map.get('month'));
      const day = Number(map.get('day'));
      const hour = Number(map.get('hour'));
      const minute = Number(map.get('minute'));
      const second = Number(map.get('second'));

      if ([year, month, day, hour, minute, second].some(part => Number.isNaN(part))) {
        return formatDate(value, format, this.locale);
      }

      const monthShort = new Intl.DateTimeFormat(this.locale, { timeZone, month: 'short' }).format(value);
      const monthLong = new Intl.DateTimeFormat(this.locale, { timeZone, month: 'long' }).format(value);
      const twelveHour = ((hour + 11) % 12) + 1;
      const expandedFormat = this.expandNamedFormat(format);
      const tokenMap: Record<string, string> = {
        yyyy: String(year).padStart(4, '0'),
        yy: String(year).slice(-2),
        y: String(year),
        MMMM: monthLong,
        MMM: monthShort,
        MM: String(month).padStart(2, '0'),
        M: String(month),
        dd: String(day).padStart(2, '0'),
        d: String(day),
        HH: String(hour).padStart(2, '0'),
        H: String(hour),
        hh: String(twelveHour).padStart(2, '0'),
        h: String(twelveHour),
        mm: String(minute).padStart(2, '0'),
        m: String(minute),
        ss: String(second).padStart(2, '0'),
        s: String(second),
        SSS: String(value.getMilliseconds()).padStart(3, '0'),
        a: hour >= 12 ? 'PM' : 'AM',
      };
      const tokenPattern = /yyyy|yy|y|MMMM|MMM|MM|M|dd|d|HH|H|hh|h|mm|m|ss|s|SSS|a/g;

      return expandedFormat.replace(tokenPattern, (token) => tokenMap[token] ?? token);
    } catch {
      return formatDate(value, format, this.locale);
    }
  }

  private expandNamedFormat(format: string): string {
    switch (format) {
      case 'short':
        return 'M/d/yy, h:mm a';
      case 'medium':
        return 'MMM d, y, h:mm:ss a';
      case 'shortTime':
        return 'h:mm a';
      case 'mediumTime':
        return 'h:mm:ss a';
      default:
        return format;
    }
  }

  private normalizeTimeZoneSuffix(raw: string): string {
    const trimmed = raw.trim();
    if (trimmed === 'GMT') {
      return 'UTC';
    }

    if (trimmed.startsWith('GMT')) {
      return trimmed.replace(/^GMT/, 'UTC');
    }

    return trimmed;
  }

  private formatUtcOffset(offsetHours: number): string {
    if (offsetHours === 0) {
      return 'UTC';
    }

    const sign = offsetHours > 0 ? '+' : '-';
    const absolute = Math.abs(offsetHours);
    const hours = Math.trunc(absolute);
    const minutes = Math.round((absolute - hours) * 60);

    if (minutes === 0) {
      return `UTC${sign}${hours}`;
    }

    return `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
  }

  private resolveSiteId(siteId?: number | null): number | null {
    if (typeof siteId === 'number' && Number.isFinite(siteId)) {
      return siteId;
    }

    const selectedSites = Global.User.currentUser?.SelectedOrganizationSites ?? [];
    if (selectedSites.length === 1 && typeof selectedSites[0]?.Id === 'number') {
      return selectedSites[0].Id;
    }

    return null;
  }
}
